import { useCallback, useEffect } from 'react';

/**
 * Returns true if the event target is an element that accepts text input.
 * Used to prevent keyboard shortcuts from firing while the user is typing.
 */
const isInteractiveElement = target => {
	if (!target) return false;

	const tagName = target.tagName?.toLowerCase();
	if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return true;
	if (target.isContentEditable) return true;

	return false;
};

/**
 * Custom hook that listens for a keydown event and runs a callback function
 * if the key matches the target key.
 *
 * The callback is suppressed when the user is focused on an interactive element
 * (input, textarea, select, contenteditable) to prevent shortcuts from
 * interfering with normal text entry.
 *
 * @param {string} targetKey - Key that must be pressed for the callback to run
 * @param {boolean} condition - Condition that must be met for the callback to run
 * @param {Function} callback - Callback function to run if the key matches
 */
export default function useKeyboardShortcut(targetKey, condition, callback) {
	const handleKeyDown = useCallback(
		event => {
			// Never fire shortcuts when the user is typing in a form element
			if (isInteractiveElement(event.target)) return;

			if (
				event.key?.toString().toLowerCase() === targetKey.toString().toLowerCase() &&
				condition
			) {
				callback();
			}
		},
		[targetKey, condition, callback]
	);

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);
}
